// Dialog body for configuring Rapid Reload package and payment readiness.

import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AlertTriangle, CircleCheck, CircleX } from 'lucide-react';
import { cn } from '@/utilities/cn';
import { Spinner } from '@/components/ui/spinner';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { AnalyticsSurface } from '../../../types/analytics';
import { RAPID_RELOAD_NONE_VALUE, useProductRapidReload } from '../../../hooks';
import {
  formatProductPrice,
  getProductLoonsGranted,
} from '@airhead-games/shared/utilities';

interface ManageRapidReloadProps {
  onClose: () => void;
  onSavingChange?: (isSaving: boolean) => void;
  knownHasSavedMethod?: boolean;
  analyticsSurface?: AnalyticsSurface;
  portalReturnUrl?: string;
}

const ManageRapidReload: React.FC<ManageRapidReloadProps> = ({
  onClose,
  onSavingChange,
  knownHasSavedMethod,
  analyticsSurface = 'library_rapid_reload',
  portalReturnUrl = '/library?dialog=rapid-reload',
}) => {
  const {
    products,
    productsLoading,
    productsError,
    selectedRapidReloadProduct,
    toggleGroupValue,
    rapidReloadError,
    isSavingRapidReload,
    billingPortalUrl,
    portalLinkLoading,
    paymentMethodStatus,
    selectionBlockedMessage,
    canConfirmRapidReload,
    handlePortalLinkClick,
    handleToggleValueChange,
    handleSave,
    handleCancel,
  } = useProductRapidReload({
    onClose,
    onSavingChange,
    knownHasSavedMethod,
    analyticsSurface,
    portalReturnUrl,
  });

  return (
    <Card className="w-full max-w-sm">
      <CardHeader>
        <CardTitle>Manage Rapid Reload</CardTitle>
        <CardDescription>
          Top up your Loons with a single click. You must have a saved payment
          method in the Stripe Customer Portal and a preferred package to use
          this feature.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <div className="flex flex-col gap-6">
          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor="rapid-reload-payment-status">
                Payment Method
              </Label>
              <div className="ml-auto flex items-center gap-1.5">
                {portalLinkLoading && !billingPortalUrl ? (
                  <Spinner className="size-4" />
                ) : billingPortalUrl ? (
                  <Button variant="link" asChild className="h-auto p-0">
                    <a
                      href={billingPortalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block text-sm underline-offset-4 hover:underline"
                      onClick={handlePortalLinkClick}
                    >
                      Manage in Stripe
                      <span className="sr-only"> (opens in new tab)</span>
                    </a>
                  </Button>
                ) : null}
              </div>
            </div>
            <div
              id="rapid-reload-payment-status"
              role="status"
              aria-live="polite"
              className={cn(
                'flex h-8 w-full min-w-0 items-center gap-2 rounded-lg border border-input bg-muted px-2.5 text-sm',
                'pointer-events-none cursor-default text-foreground'
              )}
            >
              {paymentMethodStatus.state === 'loading' ? (
                <Spinner className="size-4 shrink-0" />
              ) : paymentMethodStatus.state === 'success' ? (
                <CircleCheck
                  className="size-4 shrink-0 text-primary"
                  aria-hidden="true"
                />
              ) : paymentMethodStatus.state === 'unavailable' ? (
                <AlertTriangle
                  className="size-4 shrink-0 text-warning"
                  aria-hidden="true"
                />
              ) : (
                <CircleX
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              )}
              <span className="min-w-0 flex-1 truncate text-left">
                {paymentMethodStatus.message}
              </span>
            </div>
          </div>

          <div className="grid gap-2">
            <div className="flex items-center">
              <Label id="rapid-reload-loon-pack-label">
                Preferred Loon Pack
              </Label>
              <span
                className="ml-auto inline-block min-w-[6.5rem] text-right text-sm font-medium tabular-nums text-foreground"
                aria-live="polite"
              >
                {selectedRapidReloadProduct ? (
                  formatProductPrice(selectedRapidReloadProduct).price
                ) : (
                  <span
                    className="pointer-events-none select-none opacity-0"
                    aria-hidden="true"
                  >
                    $00.00
                  </span>
                )}
              </span>
            </div>
            {productsLoading ? (
              <div
                className="flex h-8 items-center gap-2 text-muted-foreground"
                role="status"
                aria-label="Loading preferred loon packs"
              >
                <Spinner className="size-4" />
              </div>
            ) : productsError ? (
              <p
                className="flex min-h-8 items-center text-destructive text-sm"
                role="alert"
              >
                {productsError}
              </p>
            ) : (
              <ToggleGroup
                type="single"
                variant="outline"
                spacing={0}
                value={toggleGroupValue}
                onValueChange={handleToggleValueChange}
                aria-labelledby="rapid-reload-loon-pack-label"
                className="flex h-8 w-full min-w-0 items-stretch"
              >
                <ToggleGroupItem
                  value={RAPID_RELOAD_NONE_VALUE}
                  className="min-h-8 min-w-0 flex-1 shrink"
                >
                  None
                </ToggleGroupItem>
                {products.map((product) => (
                  <ToggleGroupItem
                    key={product.stripePriceId}
                    value={product.stripePriceId}
                    className="min-h-8 min-w-0 flex-1 shrink"
                  >
                    {String(getProductLoonsGranted(product) ?? '?')}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            )}
            {selectionBlockedMessage ? (
              <p className="text-destructive text-sm" role="alert">
                {selectionBlockedMessage}
              </p>
            ) : null}
          </div>

          {rapidReloadError ? (
            <p className="text-destructive text-sm" role="alert">
              {rapidReloadError}
            </p>
          ) : null}
        </div>
      </CardContent>

      <CardFooter className="flex-col gap-2">
        <Button
          type="button"
          className="w-full"
          disabled={!canConfirmRapidReload}
          onClick={() => {
            void handleSave();
          }}
        >
          {isSavingRapidReload ? (
            <Spinner className="size-4 text-primary-foreground" />
          ) : (
            'Confirm'
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={isSavingRapidReload}
          onClick={handleCancel}
        >
          Cancel
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ManageRapidReload;
